import { memo, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Link2, Loader } from 'lucide-react';
import { format } from 'date-fns';
import { notesApiService } from '../../services/notesApi';
import type { StaffCitation } from '../../types/staff';
import { CITATION_VALENCES } from '../../types/staff';
import type { TicketItem } from '../../types/player';
import { TicketIdLink } from '../TicketIdLink';
import { TicketHealthBadge } from './TicketHealthBadge';
import { formatSeconds, prettyFacet, summarizeCitations, type TicketGroup } from '../../utils/staffProfile';
import { parseTicketId } from '../../utils/ticketsx';
import { cn } from '../../utils/cn';

interface StaffEvidenceLogProps {
  citations: Record<string, StaffCitation>;
  /** Unix seconds the payload was generated; relative times are frozen at this moment. */
  generatedAt: number;
}

type ValenceFilter = 'all' | (typeof CITATION_VALENCES)[number];

const ITEM_LABEL: Record<string, string> = {
  note: 'Note',
  warning: 'Warning',
  ban: 'Ban',
  incident: 'Incident',
  build: 'Build',
  build_project: 'Build project',
  event: 'Event',
  event_project: 'Event project',
};

const DOT: Record<string, string> = {
  good: 'bg-green-400 shadow-[0_0_8px_rgb(74_222_128/0.6)]',
  neutral: 'bg-glass-400',
  poor: 'bg-red-400 shadow-[0_0_8px_rgb(248_113_113/0.6)]',
};

const FILTER_ACTIVE: Record<string, string> = {
  all: 'border-accent-400 text-accent-300 bg-accent-500/15',
  good: 'border-green-400/60 text-green-300 bg-green-500/15',
  neutral: 'border-glass-300/60 text-white bg-glass-200/15',
  poor: 'border-red-400/60 text-red-300 bg-red-500/15',
};

function itemLink(item: TicketItem): string | null {
  switch (item.item_type as string) {
    case 'note':
    case 'warning':
      return `/notes-warnings?highlight=${encodeURIComponent(item.item_id)}`;
    case 'ban':
      return '/bans';
    case 'incident':
      return `/incidents/${encodeURIComponent(item.item_id)}`;
    case 'build':
      return `/build-team/builds/${encodeURIComponent(item.item_id)}`;
    case 'build_project':
      return `/build-team/projects/${encodeURIComponent(item.item_id)}`;
    case 'event':
      return `/event-team/events/${encodeURIComponent(item.item_id)}`;
    case 'event_project':
      return `/event-team/projects/${encodeURIComponent(item.item_id)}`;
    default:
      return null;
  }
}

/** "Linked in ARGUS" reverse lookup for one ticket, fetched lazily when expanded. */
const LinkedItems = ({ ticketId }: { ticketId: string }) => {
  const parsed = parseTicketId(ticketId);
  const [items, setItems] = useState<TicketItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (parsed === null) return;
    let cancelled = false;
    notesApiService
      .getItemsForTicket(parsed)
      .then((result) => {
        if (!cancelled) setItems(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Lookup failed');
      });
    return () => {
      cancelled = true;
    };
  }, [parsed]);

  if (parsed === null) return null;

  return (
    <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-accent-500/5 border border-accent-500/15 text-xs text-glass-400 flex flex-wrap items-center gap-2">
      <Link2 className="w-3.5 h-3.5 flex-shrink-0 text-accent-300" aria-hidden="true" />
      <span className="font-semibold uppercase tracking-[0.14em] text-[10px]">Linked in ARGUS</span>
      {error ? (
        <span className="text-yellow-300">{error}</span>
      ) : items === null ? (
        <Loader className="w-3.5 h-3.5 animate-spin" />
      ) : items.length === 0 ? (
        <span className="italic">Not linked to any note, ban or incident.</span>
      ) : (
        items.map((item) => {
          const href = itemLink(item);
          const label = `${ITEM_LABEL[item.item_type] ?? item.item_type} ${item.item_id}`;
          return href ? (
            <Link
              key={`${item.item_type}-${item.item_id}`}
              to={href}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent-500/15 text-accent-300 border border-accent-500/30 hover:bg-accent-500/25 font-mono"
            >
              {label}
            </Link>
          ) : (
            <span key={`${item.item_type}-${item.item_id}`} className="font-mono">{label}</span>
          );
        })
      )}
    </div>
  );
};

const ObservationRow = memo(({ citation, generatedAt }: { citation: StaffCitation; generatedAt: number }) => (
  <li className="relative pl-9 pr-4 py-3">
    <span className={cn('absolute left-[13px] top-[19px] w-2.5 h-2.5 rounded-full ring-2 ring-[var(--glass-bg)]', DOT[citation.valence] ?? DOT.neutral)} aria-label={citation.valence} />
    <p className="text-sm text-white leading-relaxed">{citation.fact}</p>
    {citation.quote && (
      <blockquote className="mt-2 px-3 py-1.5 border-l-2 border-accent-400/60 bg-glass-100/10 rounded-r-md text-xs text-glass-300 font-mono leading-relaxed break-words">
        “{citation.quote}”
      </blockquote>
    )}
    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-mono text-glass-500">
      <span className="uppercase tracking-[0.12em] text-glass-400">{prettyFacet(citation.facet)}</span>
      <span>#{citation.id}</span>
      {generatedAt > 0 && <span>{formatSeconds(generatedAt - citation.observed_at)} ago</span>}
      <span title="Observed at">{format(new Date(citation.observed_at * 1000), 'd MMM yyyy HH:mm')}</span>
      {citation.seen_count > 1 && <span>seen ×{citation.seen_count}</span>}
    </div>
  </li>
));
ObservationRow.displayName = 'ObservationRow';

const TicketCard = memo(
  ({ group, generatedAt, visible }: { group: TicketGroup; generatedAt: number; visible: StaffCitation[] }) => {
    const [expanded, setExpanded] = useState(false);
    const isTicket = group.ticketId !== 'unknown';
    return (
      <article className="rounded-xl border border-glass-200/15 bg-glass-100/5 overflow-hidden transition-colors hover:border-glass-200/30">
        <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 bg-glass-100/10 border-b border-glass-200/10">
          {isTicket ? (
            <TicketIdLink ticketId={group.ticketId} className="font-mono text-sm font-semibold">
              Ticket #{group.ticketId}
            </TicketIdLink>
          ) : (
            <span className="font-mono text-sm text-glass-400">No ticket</span>
          )}
          <TicketHealthBadge health={group.health} emptyLabel="open" />
          <span className="ml-auto text-[11px] font-mono text-glass-500 whitespace-nowrap tabular-nums">
            {visible.length}/{group.citations.length} obs
          </span>
          {isTicket && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className={cn(
                'inline-flex items-center gap-1 px-2 h-7 rounded-md text-[11px] font-semibold border transition-colors touch-manipulation',
                expanded
                  ? 'border-accent-400/60 text-accent-300 bg-accent-500/10'
                  : 'border-glass-200/20 text-glass-400 hover:text-white hover:border-glass-300/40',
              )}
              aria-expanded={expanded}
              title="Items linked to this ticket in ARGUS"
            >
              <Link2 className="w-3.5 h-3.5" />
              Links
              <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', expanded && 'rotate-180')} />
            </button>
          )}
        </header>
        {expanded && isTicket && (
          <div className="pt-3">
            <LinkedItems ticketId={group.ticketId} />
          </div>
        )}
        <ul className="relative divide-y divide-glass-200/10 before:absolute before:left-[17px] before:top-0 before:bottom-0 before:w-px before:bg-glass-200/15">
          {visible.map((c) => (
            <ObservationRow key={c.id} citation={c} generatedAt={generatedAt} />
          ))}
        </ul>
      </article>
    );
  },
);
TicketCard.displayName = 'TicketCard';

/** Every observation as a per-ticket timeline, with valence and facet filters. */
export const StaffEvidenceLog = ({ citations, generatedAt }: StaffEvidenceLogProps) => {
  const summary = useMemo(() => summarizeCitations(citations), [citations]);
  const [valence, setValence] = useState<ValenceFilter>('all');
  const [facet, setFacet] = useState<string>('all');

  const filtered = useMemo(
    () =>
      summary.byTicket
        .map((group) => ({
          group,
          visible: group.citations.filter(
            (c) => (valence === 'all' || c.valence === valence) && (facet === 'all' || c.facet === facet),
          ),
        }))
        .filter((entry) => entry.visible.length > 0),
    [summary, valence, facet],
  );
  const shown = filtered.reduce((n, entry) => n + entry.visible.length, 0);

  if (summary.total === 0) {
    return <p className="text-sm text-glass-500 italic">No observations recorded in this window.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {(['all', ...CITATION_VALENCES] as ValenceFilter[]).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setValence(v)}
            aria-pressed={valence === v}
            className={cn(
              'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border transition-colors touch-manipulation',
              valence === v ? FILTER_ACTIVE[v] : 'border-glass-200/20 text-glass-400 hover:text-white hover:border-glass-300/40',
            )}
          >
            {v !== 'all' && <span className={cn('w-1.5 h-1.5 rounded-full', DOT[v]?.split(' ')[0])} aria-hidden="true" />}
            <span className="capitalize">{v}</span>
            <span className="font-mono tabular-nums opacity-70">{v === 'all' ? summary.total : summary.byValence[v]}</span>
          </button>
        ))}
        <select
          value={facet}
          onChange={(e) => setFacet(e.target.value)}
          className="glass-select text-xs font-mono h-8 py-0 sm:ml-auto"
          aria-label="Filter by facet"
        >
          <option value="all">all facets</option>
          {summary.byFacet.map((f) => (
            <option key={f.facet} value={f.facet}>
              {prettyFacet(f.facet)} ({f.total})
            </option>
          ))}
        </select>
      </div>
      <p className="text-[11px] font-mono text-glass-500">
        {shown} of {summary.total} observations · newest and busiest tickets first
      </p>
      <div className="space-y-3">
        {filtered.map(({ group, visible }) => (
          <TicketCard key={group.ticketId} group={group} generatedAt={generatedAt} visible={visible} />
        ))}
      </div>
    </div>
  );
};
